import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, ChevronLeft, ChevronRight, Loader2, Sparkles, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { PageHeader, EmptyState } from "@/components/common";
import { UpgradeGate } from "@/components/UpgradeGate";
import AdvisorChat from "@/components/AdvisorChat";
import ContentLibrary, { ContentDialog, GenerateDialog, KIND_LABELS, MARKETING_KINDS } from "@/components/ContentLibrary";
import { api, errMsg, ymd, localToday, parseLocal } from "@/lib/api";

function ContentCalendar() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [gen, setGen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/content"); setItems(data); } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const days = useMemo(() => {
    const first = new Date(month);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    return [...Array(42)].map((_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [month]);
  const scheduled = (items || []).filter((i) => i.scheduled_date);
  const inMonth = scheduled.filter((i) => parseLocal(i.scheduled_date).getMonth() === month.getMonth() && parseLocal(i.scheduled_date).getFullYear() === month.getFullYear())
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  const unscheduled = (items || []).filter((i) => !i.scheduled_date && i.area === "marketing");
  const today = localToday();
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div data-testid="content-calendar">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" size="icon" className="bg-card" onClick={() => shift(-1)} aria-label="Previous month" data-testid="content-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="icon" className="bg-card" onClick={() => shift(1)} aria-label="Next month" data-testid="content-cal-next"><ChevronRight className="h-4 w-4" /></Button>
        <p className="text-sm font-semibold ml-1" data-testid="content-cal-label">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
        <Button size="sm" className="ml-auto" onClick={() => setGen(true)} data-testid="content-cal-generate"><Sparkles className="h-4 w-4 mr-1.5" />Plan a post</Button>
      </div>
      {items === null ? <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <div className="xl:col-span-9">
            <div className="hidden md:grid grid-cols-7 bg-card border border-border rounded-xl overflow-hidden">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-[11px] uppercase tracking-wider text-muted-foreground px-2 py-2 border-b border-border">{d}</div>)}
              {days.map((d) => {
                const key = ymd(d);
                const list = scheduled.filter((i) => i.scheduled_date === key);
                const out = d.getMonth() !== month.getMonth();
                return (
                  <div key={key} className={`min-h-[104px] border-b border-r border-border p-1.5 ${out ? "bg-muted/50" : ""}`}>
                    <p className={`text-xs mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full ${key === today ? "bg-primary text-white" : out ? "text-muted-foreground" : ""}`}>{d.getDate()}</p>
                    <div className="space-y-1">
                      {list.slice(0, 3).map((i) => (
                        <button key={i.content_id} onClick={() => setOpen(i)} data-testid="content-cal-item"
                          className="block w-full text-left text-[11px] leading-tight rounded-md bg-[hsl(174_42%_90%)] border border-[hsl(174_30%_78%)] px-1.5 py-1 truncate hover:shadow-sm">
                          {i.channel ? `${i.channel}: ` : ""}{i.title}
                        </button>
                      ))}
                      {list.length > 3 && <p className="text-[10px] text-muted-foreground">+{list.length - 3} more</p>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="md:hidden bg-card border border-border rounded-xl">
              {inMonth.length === 0 ? <EmptyState icon={CalendarDays} title="Nothing scheduled this month" /> : (
                <ul className="divide-y divide-border">
                  {inMonth.map((i) => (
                    <li key={i.content_id}><button onClick={() => setOpen(i)} className="w-full text-left p-3" data-testid="content-cal-agenda-item">
                      <p className="text-xs text-muted-foreground">{parseLocal(i.scheduled_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {i.channel || KIND_LABELS[i.kind]}</p>
                      <p className="text-sm font-medium">{i.title}</p>
                    </button></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="xl:col-span-3 bg-card border border-border rounded-xl p-4 h-fit" data-testid="content-unscheduled">
            <p className="text-sm font-semibold mb-3">Unscheduled drafts</p>
            {unscheduled.length === 0 ? <p className="text-xs text-muted-foreground">None. Every draft is on the calendar.</p> : (
              <ul className="space-y-2">
                {unscheduled.slice(0, 12).map((i) => (
                  <li key={i.content_id}><button onClick={() => setOpen(i)} className="w-full text-left rounded-lg border border-border px-3 py-2 hover:bg-muted transition-colors">
                    <Badge variant="secondary" className="font-normal text-[10px]">{KIND_LABELS[i.kind]}</Badge>
                    <p className="text-sm mt-1 line-clamp-2">{i.title}</p>
                  </button></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <ContentDialog item={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onChanged={(d) => { load(); if (d) setOpen(d); }} />
      <GenerateDialog open={gen} onOpenChange={setGen} kinds={MARKETING_KINDS} defaultKind="social_post" onCreated={(d) => { load(); setOpen(d); }} />
    </div>
  );
}

export default function Marketing() {
  return (
    <UpgradeGate entitlement="ai_marketing">
      <MarketingInner />
    </UpgradeGate>
  );
}

function MarketingInner() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "calendar";
  return (
    <div data-testid="marketing-page">
      <PageHeader title="AI Marketing Manager" subtitle="Campaigns, a content calendar, social posts, newsletters and ad copy. Everything is a draft until you publish it yourself." />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="mb-4">
          <TabsTrigger value="calendar" data-testid="marketing-tab-calendar">Content calendar</TabsTrigger>
          <TabsTrigger value="library" data-testid="marketing-tab-library">Library</TabsTrigger>
          <TabsTrigger value="chat" data-testid="marketing-tab-chat">Chat</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar"><ContentCalendar /></TabsContent>
        <TabsContent value="library"><ContentLibrary area="marketing" kinds={MARKETING_KINDS} /></TabsContent>
        <TabsContent value="chat">
          <AdvisorChat role="marketing" className="h-[calc(100vh-15rem)] min-h-[520px]" persona={{
            name: "AI Marketing Manager", icon: Megaphone, tagline: "Plans campaigns and writes copy. Saves drafts after you approve them.",
            greeting: "What are we promoting?", intro: "I know your company and its finances. Ask for a campaign plan, a month of social posts, a newsletter or ad copy, and I'll put each piece forward for your approval before saving it to the calendar.",
            placeholder: "e.g. Plan a 4-week LinkedIn campaign for our new service",
            suggestions: ["Plan a 4-week campaign to win new clients", "Write 3 LinkedIn posts for next week and schedule them", "Draft this month's customer newsletter",
              "Write Google search ad copy for our main service", "Build a content calendar for next month", "How much should we spend on marketing?"],
          }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
